import { useMemo, useCallback, useState, useEffect } from "react";
import { Text, Flex, Spinner } from "@radix-ui/themes";
import { CrossCircledIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import type { SnippetDetail, ReleaseAccessSnippet } from "@/v2/entities/KnowledgeSnippet";
import {
  getActiveVersion,
  useDeleteSnippet,
  useUpdateSnippetVersion,
  useVerifySnippetVersion,
} from "@/v2/entities/KnowledgeSnippet";
import { VersionHistorySidePanel } from "./VersionHistorySidePanel";
import { VerifySnippetDialog } from "./VerifySnippetDialog";
import { DeleteSnippetDialog } from "./DeleteSnippetDialog";
import { SnippetDetailHeader } from "./SnippetDetailHeader";
import { SnippetDetailMetadata } from "./SnippetDetailMetadata";
import { SnippetDetailContent } from "./SnippetDetailContent";
import styles from "./SnippetDetail.module.scss";

interface SnippetDetailProps {
  snippetDetail: SnippetDetail | null | undefined;
  isLoading?: boolean;
  isError?: boolean;
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string | null) => void;
  onRefreshSnippet: () => Promise<unknown>;
}

export function SnippetDetail({
  snippetDetail,
  isLoading,
  isError,
  selectedVersionId,
  onSelectVersion,
  onRefreshSnippet,
}: SnippetDetailProps) {
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string | null>(null);

  const { mutateAsync: deleteSnippet } = useDeleteSnippet();
  const {
    mutateAsync: updateSnippetVersion,
    isLoading: isUpdatingSnippetVersion,
  } = useUpdateSnippetVersion();
  const { mutateAsync: verifySnippetVersion } = useVerifySnippetVersion();

  // Get the active version (verified or most recent)
  const activeVersion = useMemo(() => {
    if (!snippetDetail) return null;
    if (selectedVersionId) {
      // User has manually selected a version
      return (
        snippetDetail.versions.find((v) => v.id === selectedVersionId) ||
        getActiveVersion(snippetDetail)
      );
    }
    return getActiveVersion(snippetDetail);
  }, [snippetDetail, selectedVersionId]);

  // Determine if viewing a historical version (read-only)
  const isViewingHistoricalVersion = useMemo(() => {
    if (!snippetDetail || !activeVersion) return false;
    const currentVersion = getActiveVersion(snippetDetail);
    if (!currentVersion) return false;
    return selectedVersionId !== null && activeVersion.id !== currentVersion.id;
  }, [snippetDetail, activeVersion, selectedVersionId]);

  // Get the content and title to display (edited or active version)
  const displayContent = editedContent ?? activeVersion?.content ?? "";

  const displayTitle = editedTitle ?? activeVersion?.title ?? "";

  // Check if content or title has been modified
  const hasUnsavedChanges =
    (editedContent !== null && editedContent !== activeVersion?.content) ||
    (editedTitle !== null && editedTitle !== activeVersion?.title);

  const handleVerifySnippetClick = useCallback(() => {
    setIsVerifyDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteSnippet = useCallback(async () => {
    if (!snippetDetail) return;
    await deleteSnippet(snippetDetail.id);
  }, [snippetDetail, deleteSnippet]);

  const handleVersionHistoryToggle = useCallback(() => {
    setIsVersionHistoryOpen((prev) => !prev);
  }, []);

  const handleVersionHistoryClose = useCallback(() => {
    setIsVersionHistoryOpen(false);
  }, []);

  const handleVersionSelect = useCallback(
    (versionId: string | null) => {
      onSelectVersion(versionId);
      setEditedContent(null);
      setEditedTitle(null);
    },
    [onSelectVersion]
  );

  const handleContentChange = useCallback((newContent: string) => {
    setEditedContent(newContent);
  }, []);

  const handleTitleChange = useCallback((newTitle: string) => {
    setEditedTitle(newTitle);
  }, []);

  useEffect(() => {
    if (!snippetDetail || !selectedVersionId) {
      return;
    }

    const versionExists = snippetDetail.versions.some(
      (version) => version.id === selectedVersionId
    );

    if (!versionExists) {
      onSelectVersion(null);
    }
  }, [snippetDetail, selectedVersionId, onSelectVersion]);

  useEffect(() => {
    setEditedContent(null);
    setEditedTitle(null);
  }, [snippetDetail?.id, selectedVersionId]);

  const handleSaveChanges = useCallback(async (): Promise<void> => {
    if (
      !snippetDetail ||
      !activeVersion ||
      isUpdatingSnippetVersion ||
      !hasUnsavedChanges
    )
      return;

    const finalTitle = editedTitle ?? activeVersion.title;
    const finalContent = editedContent ?? activeVersion.content;

    try {
      await updateSnippetVersion({
        snippetId: snippetDetail.id,
        versionId: activeVersion.id,
        title: finalTitle,
        content: finalContent,
      });

      let refreshSucceeded = true;
      try {
        await onRefreshSnippet();
      } catch (error) {
        console.error(
          "Failed to refresh snippet detail after saving changes",
          error
        );
        refreshSucceeded = false;
      }

      if (refreshSucceeded) {
        setEditedContent(null);
        setEditedTitle(null);
      }
    } catch (error) {
      console.error("Failed to save snippet changes", error);
      toast.error("Failed to save snippet changes");
    }
  }, [
    snippetDetail,
    activeVersion,
    editedTitle,
    editedContent,
    updateSnippetVersion,
    onRefreshSnippet,
    isUpdatingSnippetVersion,
    hasUnsavedChanges,
  ]);

  const handleVerifySnippet = useCallback(
    async (
      versionName: string,
      approvalNote?: string,
      releaseAccessSnippets?: ReleaseAccessSnippet[]
    ): Promise<void> => {
      if (!snippetDetail || !activeVersion) return;

      try {
        // First save any unsaved changes
        if (hasUnsavedChanges) {
          await handleSaveChanges();
        }

        // Then verify the version
        await verifySnippetVersion({
          snippetId: snippetDetail.id,
          versionId: activeVersion.id,
          versionName,
          approvalNote,
          releaseAccessSnippets,
        });

        try {
          await onRefreshSnippet();
        } catch (error) {
          console.error(
            "Failed to refresh snippet detail after verification",
            error
          );
        }

        onSelectVersion(null);

        // Reset edited states
        setEditedContent(null);
        setEditedTitle(null);

        // Show success toast
        toast.success("Snippet verified successfully");
      } catch (error) {
        console.error("Failed to verify snippet", error);
        toast.error("Failed to verify snippet");
        throw error;
      }
    },
    [
      snippetDetail,
      activeVersion,
      hasUnsavedChanges,
      handleSaveChanges,
      verifySnippetVersion,
      onRefreshSnippet,
      onSelectVersion,
    ]
  );

  // Loading state
  if (isLoading) {
    return (
      <Flex
        className={styles.loadingState}
        direction="column"
        align="center"
        justify="center"
        gap="3"
      >
        <Spinner size="3" />
        <Text size="3" color="gray">
          Loading snippet...
        </Text>
      </Flex>
    );
  }

  // Error state
  if (isError) {
    return (
      <Flex
        className={styles.errorState}
        direction="column"
        align="center"
        justify="center"
        gap="2"
      >
        <CrossCircledIcon width={24} height={24} />
        <Text size="3" color="red">
          Error loading snippet
        </Text>
        <Text size="2" color="gray">
          Please try again later
        </Text>
      </Flex>
    );
  }

  // Empty state
  if (!snippetDetail || !activeVersion) {
    return (
      <Flex className={styles.emptyState} align="center" justify="center">
        <Text size="3" color="gray">
          Select a snippet to view its content
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.snippetDetailWrapper} gap="2">
      <Flex className={styles.snippetDetailContainer} direction="column" p="5">
        <SnippetDetailHeader
          snippetId={snippetDetail.id}
          accessLabels={snippetDetail.accessLabels}
          status={activeVersion.status}
          isInternal={snippetDetail.isInternal}
          onVerifySnippet={handleVerifySnippetClick}
          onSaveChanges={handleSaveChanges}
          onDelete={handleDeleteClick}
          onVersionHistory={handleVersionHistoryToggle}
          hasUnsavedChanges={hasUnsavedChanges}
          isViewingHistoricalVersion={isViewingHistoricalVersion}
          onRefreshSnippet={async () => {
            await onRefreshSnippet();
          }}
          isSavingChanges={isUpdatingSnippetVersion}
          isCurrentVersionVerified={snippetDetail.isCurrentVersionVerified}
          isVerifiedVersionAvailable={snippetDetail.isVerifiedVersionAvailable}
        />

        <SnippetDetailMetadata
          title={displayTitle}
          tags={snippetDetail.tags}
          approvalNote={activeVersion.approvalNote || ""}
          onTitleChange={handleTitleChange}
          editable={!isViewingHistoricalVersion}
        />

        <SnippetDetailContent
          content={displayContent}
          onChange={handleContentChange}
          editable={!isViewingHistoricalVersion}
          snippetId={snippetDetail.id}
        />
      </Flex>

      {/* Version History Side Panel */}
      {isVersionHistoryOpen && snippetDetail.versions.length > 0 && (
        <VersionHistorySidePanel
          snippetDetail={snippetDetail}
          selectedVersionId={selectedVersionId}
          onVersionSelect={handleVersionSelect}
          onClose={handleVersionHistoryClose}
        />
      )}

      {/* Verify Snippet Dialog */}
      <VerifySnippetDialog
        open={isVerifyDialogOpen}
        onOpenChange={setIsVerifyDialogOpen}
        defaultVersionName={activeVersion.versionName || undefined}
        hasUnsavedChanges={hasUnsavedChanges}
        releaseAccessSnippets={snippetDetail?.releaseAccessSnippets || []}
        onVerify={handleVerifySnippet}
      />

      {/* Delete Snippet Dialog */}
      <DeleteSnippetDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        snippetTitle={activeVersion.title}
        onDeleteSnippet={handleDeleteSnippet}
      />
    </Flex>
  );
}
